import { useEffect, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import Dropdown from './Dropdown';
import TextFieldCardCounter from './TextFieldCardCounter';
import IcTagSvg from '../../assets/images/svg/ic-tag.svg';
import IcLinkSvg from '../../assets/images/svg/ic-link.svg';
import IcPhoneSvg from '../../assets/images/svg/ic-phone.svg';
import IcXCircle from '../../assets/images/svg/ic-x-circle.svg';
import { color } from '../../app/theme';
import {
  validateHttpsAndHttpPath,
  validateNo8Path,
  transformPhone,
  validatePhone,
} from '../../app/utils';
import CONSTANTS from '../../app/constants';

const useStyles = makeStyles(
  () => ({
    root: {
      padding: '0 16px',
    },
    deleteContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginRight: -16,
      marginBottom: 4,
      '& > img': {
        cursor: 'pointer',
        display: 'block',
      },
    },
    dropdown: {
      margin: '0 0 8px 0',
    },
    tagContainer: {
      display: 'flex',
      alignItems: 'center',
      margin: '8px 0',
      '& > img': {
        display: 'block',
        width: 24,
        Height: 24,
        margin: '0 6px 0 0',
      },
    },
    textField: {
      display: 'block',
      flex: '1 1 auto',
      width: '100%',
      height: 44,
      fontSize: 15,
      lineHeight: '21px',
      outline: 'none',
      padding: '0 16px',
      borderRadius: 22,
      border: `1px solid ${color.$10}`,
      backgroundColor: color.$7,
      '&:focus': {
        borderColor: color.$1,
      },
    },
    'textField--error': {
      border: `1px solid ${color.$17} !important`,
    },
  }),
  {
    name: 'OptionalCardButton',
  },
);

function OptionalCardButton({
  type,
  title,
  data,
  tags,
  onChange,
  showDeleteIcon,
  onDelete,
  isModified,
  isShareLink,
  isShowRanking,
  buttonIndex,
  isCampaign,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [localType, setLocalType] = useState(type);
  const [localTitle, setLocalTitle] = useState(title);
  const [localData, setLocalData] = useState(data);
  const [localTags, setLocalTags] = useState(tags);
  const [localTag, setLocalTag] = useState(tags.length > 0 ? tags[0] : '');
  const [buttonTypes, setButtonTypes] = useState([]);

  useEffect(() => {
    let tempButtonTypes = [
      { text: t('templates.button-type.postback'), value: 'postback' },
      { text: t('templates.button-type.url'), value: 'url' },
      { text: t('templates.button-type.phone'), value: 'phone' },
      // { text: t('templates.button-type.location'), value: 'location' },
    ];

    if (isShareLink) {
      if (buttonIndex === 0) {
        tempButtonTypes = [
          {
            text: t('template.buttonType.shareLink'),
            value: CONSTANTS.TEMPLATE_BUTTON_TYPE.SHARE_LINK,
          },
        ];
      } else {
        tempButtonTypes = [
          { text: t('templates.button-type.postback'), value: 'postback' },
          { text: t('templates.button-type.url'), value: 'url' },
        ];
      }
    } else if (isShowRanking) {
      if (buttonIndex === 0) {
        tempButtonTypes = [
          {
            text: t('growth-tool.create-campaign.show-ranking-button'),
            value: CONSTANTS.TEMPLATE_BUTTON_TYPE.POSTBACK,
          },
        ];
      } else {
        tempButtonTypes = [
          { text: t('templates.button-type.postback'), value: 'postback' },
          { text: t('templates.button-type.url'), value: 'url' },
        ];
      }
    }

    setButtonTypes(tempButtonTypes);
  }, []);

  function handleChangeTitle(value) {
    if (
      localType === CONSTANTS.TEMPLATE_BUTTON_TYPE.POSTBACK ||
      localType === CONSTANTS.TEMPLATE_BUTTON_TYPE.SHARE_LINK
    ) {
      setLocalTitle(value);
      setLocalData(value);
      onChange({
        type: localType,
        title: value,
        data: value,
        tags: localTags,
      });
    } else {
      setLocalTitle(value);
      onChange({
        type: localType,
        title: value,
        data: localData,
        tags: localTags,
      });
    }
  }

  function handleChangeData(e) {
    const tempData = e.target.value;
    setLocalData(tempData);
    onChange({
      type: localType,
      title: localTitle,
      data: localType === 'phone' ? transformPhone(tempData) : tempData,
      tags: localTags,
    });
  }

  function handleChangeTag(e) {
    const v = e.target.value;
    setLocalTag(v);
    setLocalTags([v]);
    onChange({
      type: localType,
      title: localTitle,
      data: localData,
      tags: [v],
    });
  }

  function handleChangeType(newType) {
    setLocalType(newType);

    setLocalTitle('');
    setLocalData('');
    setLocalTag('');
    setLocalTags([]);
    onChange({
      type: newType,
      title: '',
      data: '',
      tags: [],
    });
  }

  function isShowTag() {
    return (
      !isShowRanking &&
      !isShareLink &&
      !isCampaign &&
      (localType === 'postback' || localType === 'url')
    );
  }
  function isShowDeleteIcon() {
    if (isShareLink || isShowRanking) {
      return buttonIndex > 0;
    }
    return true;
  }

  return (
    <div className={classes.root}>
      {showDeleteIcon && isShowDeleteIcon() && (
        <div className={classes.deleteContainer}>
          <img src={IcXCircle} alt="Delete button" onClick={onDelete} />
        </div>
      )}
      <Dropdown
        className={classes.dropdown}
        menuItems={buttonTypes}
        value={localType}
        onChange={handleChangeType}
      />
      <TextFieldCardCounter
        placeholder={t('templates.button-placeholder')}
        maxCount={20}
        value={localTitle}
        onChange={handleChangeTitle}
        status={isModified && localTitle === '' ? 'error' : undefined}
      />
      {localType === 'url' && (
        <div className={classes.tagContainer}>
          <img src={IcLinkSvg} alt="Link" />
          <input
            className={classnames(classes.textField, {
              [classes['textField--error']]:
                isModified &&
                (localData === '' ||
                  (!validateHttpsAndHttpPath(localData) &&
                    !validateNo8Path(localData))),
            })}
            value={localData}
            placeholder={t('templates.url-placeholder')}
            onChange={handleChangeData}
          />
        </div>
      )}
      {localType === 'phone' && (
        <div className={classes.tagContainer}>
          <img src={IcPhoneSvg} alt="Tag" />
          <input
            className={classnames(classes.textField, {
              [classes['textField--error']]:
                isModified &&
                (localData === '' || !validatePhone(transformPhone(localData))),
            })}
            value={localData}
            placeholder={t('templates.phone-placeholder')}
            onChange={handleChangeData}
          />
        </div>
      )}
      {isShowTag() && (
        <div className={classes.tagContainer}>
          <img src={IcTagSvg} alt="Tag" />
          <input
            type="text"
            className={classes.textField}
            placeholder={t('templates.tags-platform')}
            value={localTag}
            onChange={handleChangeTag}
          />
        </div>
      )}
    </div>
  );
}

OptionalCardButton.defaultProps = {
  title: '',
  data: '',
  tags: [],
  onChange: () => {},
  showDeleteIcon: false,
  onDelete: () => {},
  isModified: false,
  isShareLink: false,
  isShowRanking: false,
  isCampaign: false,
  buttonIndex: 0,
};

OptionalCardButton.propTypes = {
  type: PropTypes.oneOf(['postback', 'url', 'phone', 'share-link', 'location'])
    .isRequired,
  title: PropTypes.string,
  data: PropTypes.string,
  tags: PropTypes.array,
  onChange: PropTypes.func,
  showDeleteIcon: PropTypes.bool,
  onDelete: PropTypes.func,
  isModified: PropTypes.bool,
  isShareLink: PropTypes.bool,
  isShowRanking: PropTypes.bool,
  isCampaign: PropTypes.bool,
  buttonIndex: PropTypes.number,
};

export default OptionalCardButton;
